import React, { useState, useRef } from "react";
import axios from "axios";
import swal from "sweetalert";

const KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const INITIAL = { current_password: "", new_password: "", new_confirm_password: "" };

const EyeIcon = ({ visible }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
    {!visible && <path d="M3 3 21 21" />}
  </svg>
);

export default function Checkout() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [visible, setVisible] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const busy = useRef(false);

  const change = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: undefined, general: undefined }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy.current) return;
    if (!localStorage.getItem("auth_token")) {
      swal("Session Expired", "Please log in before changing your transaction password.", "warning");
      return;
    }

    const nextErrors = {};
    if (!form.current_password) nextErrors.current_password = "Current password is required.";
    if (!form.new_password) nextErrors.new_password = "New password is required.";
    if (!form.new_confirm_password) nextErrors.new_confirm_password = "Please confirm the new password.";
    if (form.new_password && form.current_password === form.new_password)
      nextErrors.new_password = "Choose a password different from your current password.";
    if (form.new_password && form.new_confirm_password && form.new_password !== form.new_confirm_password)
      nextErrors.new_confirm_password = "Passwords do not match.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    busy.current = true;
    setSubmitting(true);
    setErrors({});
    try {
      const response = await axios.post("/apps-tran-password-change", {
        current_password: form.current_password,
        new_password: form.new_password,
        new_confirm_password: form.new_confirm_password,
        key_s: KEY,
      });
      const result = response?.data || {};
      if (result.status === "success") {
        setForm(INITIAL);
        setVisible({});
        swal("Success", result.message || "Transaction password updated successfully.", "success");
      } else if (result.status === "fail") {
        swal("Warning", result.message || "Unable to change the transaction password.", "warning");
      } else {
        const validation = result.validation_error || {};
        setErrors(typeof validation === "object" && !Array.isArray(validation) ? validation : { general: result.message || "Please check the form." });
      }
    } catch (error) {
      const validation = error?.response?.data?.validation_error || error?.response?.data?.errors;
      if (validation && typeof validation === "object" && !Array.isArray(validation)) {
        setErrors(validation);
      } else {
        swal("Request Failed", error?.response?.data?.message || "Unable to connect. Please try again.", "error");
      }
    } finally {
      busy.current = false;
      setSubmitting(false);
    }
  };

  const fields = [
    { name: "current_password", label: "Current Transaction Password", placeholder: "Enter your current password", autoComplete: "current-password" },
    { name: "new_password", label: "New Transaction Password", placeholder: "Create a new password", autoComplete: "new-password" },
    { name: "new_confirm_password", label: "Confirm New Password", placeholder: "Re-enter your new password", autoComplete: "new-password" },
  ];
  const message = (value) => Array.isArray(value) ? value.join(" ") : (typeof value === "string" ? value : "");

  return (
    <>
      <style>{`
        html, body, #root { background: #00191D !important; }
        .rmtp, .rmtp * { box-sizing: border-box; }
        .rmtp { --navy:#00191D; --cyan:#16B8C7; --muted:#a3bbc0; min-height:100vh; width:100%; background:#00191D; color:#fff; font-family:inherit; font-size:14px; line-height:1.55; padding:20px 4px 36px; }
        .rmtp-wrap { max-width:1040px; margin:0 auto; }
        .rmtp-top { display:flex; align-items:center; justify-content:space-between; gap:14px; margin-bottom:16px; padding:18px 20px; border:1px solid rgba(22,184,199,.24); border-radius:20px; background:linear-gradient(120deg,#052a30,#00191D); animation:rmtp-in .35s ease-out both; }
        .rmtp-eyebrow { color:#65dce5; font-size:11px; font-weight:800; letter-spacing:1.3px; text-transform:uppercase; }
        .rmtp-top h1 { font-size:23px; line-height:1.25; margin:5px 0; color:#fff; font-weight:800; }
        .rmtp-top p { margin:0; color:#b4c9cc; }
        .rmtp-lock { display:grid; place-items:center; flex:none; width:52px; height:52px; border-radius:15px; background:linear-gradient(135deg,#16B8C7,#77e4ec); color:#00262c; }
        .rmtp-layout { display:grid; grid-template-columns:minmax(0,1.5fr) minmax(250px,1fr); gap:16px; align-items:start; }
        .rmtp-form { background:#08272c; border:1px solid rgba(22,184,199,.23); border-radius:22px; padding:24px; box-shadow:0 10px 35px rgba(0,0,0,.13); animation:rmtp-in .4s ease-out both; }
        .rmtp-form-head { display:flex; gap:11px; align-items:center; margin-bottom:22px; padding-bottom:17px; border-bottom:1px solid rgba(255,255,255,.1); }
        .rmtp-step { width:36px; height:36px; border-radius:11px; background:rgba(22,184,199,.12); color:#65dce5; display:grid; place-items:center; font-weight:800; }
        .rmtp-form-head h2 { color:#fff; font-size:17px; margin:0; font-weight:800; }
        .rmtp-form-head p { color:#a9c0c4; font-size:12px; margin:2px 0 0; }
        .rmtp-field { margin-bottom:18px; }
        .rmtp-label { display:block; font-size:14px; font-weight:650; margin-bottom:7px; color:#f2fcfd; }
        .rmtp-input-wrap { display:flex; align-items:center; border:1px solid #315359; background:#00191D; border-radius:12px; transition:border-color .18s, box-shadow .18s; }
        .rmtp-input-wrap:focus-within { border-color:#16B8C7; box-shadow:0 0 0 3px rgba(22,184,199,.12); }
        .rmtp-input { width:100%; min-width:0; height:48px; background:transparent !important; color:#fff !important; border:0 !important; box-shadow:none !important; outline:0 !important; padding:0 14px; font-family:inherit; font-size:14px; }
        .rmtp-input::placeholder { color:#78969b; }
        .rmtp-eye { display:grid; place-items:center; width:44px; height:44px; flex:none; border:0; background:transparent; color:#9bcbd1; cursor:pointer; border-radius:10px; }
        .rmtp-eye:hover { color:#fff; background:rgba(22,184,199,.1); }
        .rmtp-error { display:block; margin-top:6px; font-size:12px; color:#ff9d9d; }
        .rmtp-submit { display:flex; align-items:center; justify-content:center; gap:9px; width:100%; min-height:50px; border:0; border-radius:12px; padding:12px; color:#00242a; font-size:14px; font-weight:800; cursor:pointer; background:linear-gradient(110deg,#16B8C7,#74e1e9); transition:transform .18s, box-shadow .18s; }
        .rmtp-submit:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 9px 20px rgba(22,184,199,.18); }
        .rmtp-submit:disabled { opacity:.65; cursor:not-allowed; }
        .rmtp-side { display:grid; gap:14px; }
        .rmtp-white { background:#fff; color:#102f34; border-radius:20px; padding:22px; border:1px solid #dbe8e9; animation:rmtp-in .45s ease-out both; }
        .rmtp-white h3 { color:#103238; margin:0 0 10px; font-size:17px; font-weight:800; }
        .rmtp-white p { color:#526b70; margin:0 0 14px; }
        .rmtp-tips { padding:0; margin:0; list-style:none; }
        .rmtp-tips li { display:flex; gap:9px; padding:10px 0; border-top:1px solid #e9eff0; color:#36575d; }
        .rmtp-check { color:#0c8d9c; font-weight:900; }
        .rmtp-note { background:#05272d; border:1px solid rgba(22,184,199,.2); border-radius:18px; padding:17px; color:#b8ced1; }
        .rmtp-note strong { display:block; color:#69dae4; margin-bottom:5px; }
        .rmtp-note p { margin:0; }
        @keyframes rmtp-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @media(max-width:720px) { .rmtp { padding-top:10px; } .rmtp-layout { grid-template-columns:1fr; gap:12px; } .rmtp-top { padding:16px; } .rmtp-top h1 { font-size:20px; } .rmtp-form { padding:17px; } .rmtp-white { padding:17px; } }
        @media(prefers-reduced-motion:reduce) { .rmtp * { animation:none !important; transition:none !important; } }
      `}</style>
      <main className="rmtp">
        <div className="rmtp-wrap">
          <header className="rmtp-top">
            <div>
              <div className="rmtp-eyebrow">Account Security</div>
              <h1>Transaction Password</h1>
              <p>Keep your wallet and transaction approvals protected.</p>
            </div>
            <div className="rmtp-lock" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
            </div>
          </header>
          <div className="rmtp-layout">
            <section className="rmtp-form" aria-label="Change transaction password">
              <div className="rmtp-form-head">
                <div className="rmtp-step">01</div>
                <div><h2>Change Transaction Password</h2><p>Complete all fields to update your credentials.</p></div>
              </div>
              <form onSubmit={submit}>
                {fields.map((field) => (
                  <div className="rmtp-field" key={field.name}>
                    <label className="rmtp-label" htmlFor={`rmtp-${field.name}`}>{field.label}</label>
                    <div className="rmtp-input-wrap">
                      <input id={`rmtp-${field.name}`} className="rmtp-input" name={field.name}
                        type={visible[field.name] ? "text" : "password"} autoComplete={field.autoComplete}
                        placeholder={field.placeholder} value={form[field.name]} onChange={change} required
                        aria-invalid={Boolean(errors[field.name])} />
                      <button className="rmtp-eye" type="button" aria-label={`${visible[field.name] ? "Hide" : "Show"} ${field.label}`}
                        onClick={() => setVisible((prev) => ({ ...prev, [field.name]: !prev[field.name] }))}>
                        <EyeIcon visible={Boolean(visible[field.name])} />
                      </button>
                    </div>
                    {errors[field.name] && <span className="rmtp-error" role="alert">{message(errors[field.name])}</span>}
                  </div>
                ))}
                {errors.general && <p className="rmtp-error" role="alert">{message(errors.general)}</p>}
                <button className="rmtp-submit" type="submit" disabled={submitting}>
                  {submitting ? "Updating Password..." : "Update Transaction Password"}
                  {!submitting && <span aria-hidden="true">→</span>}
                </button>
              </form>
            </section>
            <aside className="rmtp-side">
              <section className="rmtp-white">
                <h3>Secure Your Transactions</h3>
                <p>A separate transaction password helps protect sensitive wallet actions.</p>
                <ul className="rmtp-tips">
                  <li><span className="rmtp-check">✓</span><span>Use a unique password you do not use elsewhere.</span></li>
                  <li><span className="rmtp-check">✓</span><span>Check that the new and confirmation passwords match.</span></li>
                  <li><span className="rmtp-check">✓</span><span>Never share your transaction password with anyone.</span></li>
                </ul>
              </section>
              <section className="rmtp-note"><strong>Security reminder</strong><p>Changing your password requires your current transaction password. If you did not request this change, do not submit the form.</p></section>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}
